import { useCallback, useContext, useRef } from 'react';

import GoogleAnalyticsContext from './context';
import { GAEventData } from './types';

/**
 * Provides (memoized) handler for sending a GAEvent, the eventData could be provided to the hook or to the handler,
 * both will be merged, one can either provide eventData to the hook and use the memoized handler as event handler or
 * provide eventData when calling the handler or provide some base data to the hook and add or override the data when
 * using the handler.
 * This hook does not allow to pass an `eventExtra` argument to the `sendEvent` as it is solely needed and will
 * complicate the logic significantly. Use low-level `useGoogleAnalytics` for such purposes.
 */
const useAnalyticsEvent = (
	baseEventData?: Partial<GAEventData>
): ((additionalEventData: Partial<GAEventData>) => void) => {
	const googleAnalyticsContext = useContext(GoogleAnalyticsContext);
	const baseEventDataRef = useRef(baseEventData);
	return useCallback((additionalEventData) => {
		baseEventDataRef.current = baseEventData;
		const enhancedEventData = { ...baseEventDataRef.current, ...additionalEventData };
		googleAnalyticsContext.sendEvent(enhancedEventData as GAEventData);
	}, []);
};

export default useAnalyticsEvent;
